import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import jwtDecode from 'jwt-decode';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user and quota data
  const fetchUserData = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data.user);
      setQuota(response.data.quota);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expired - l'intercepteur axios gérera le refresh automatiquement
          if (refreshToken) {
            // On a un refresh token, on peut tenter de se reconnecter
            setIsAuthenticated(true);
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            setCurrentUser({
              id: decoded.id,
              firstName: decoded.firstName,
              lastName: decoded.lastName,
              email: decoded.email,
              company: decoded.company,
              isVerified: decoded.isVerified,
              role: decoded.role
            });
            
            fetchUserData(token);
          } else {
            // Pas de refresh token, déconnecter
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            setCurrentUser(null);
            setQuota(null);
          }
        } else {
          // Valid token
          setIsAuthenticated(true);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // ✅ Set currentUser immédiatement avec les données du JWT
          // Cela évite le wizard de s'afficher pendant que fetchUserData est en cours
          setCurrentUser({
            id: decoded.id,
            firstName: decoded.firstName,
            lastName: decoded.lastName,
            email: decoded.email,
            company: decoded.company, // ✅ Important: présent depuis le fix JWT
            isVerified: decoded.isVerified,
            role: decoded.role
          });
          
          // Fetch full user data including quota (asynchrone, mettra à jour après)
          fetchUserData(token);
        }
      } catch (error) {
        // Invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setIsAuthenticated(false);
        setCurrentUser(null);
        setQuota(null);
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setCurrentUser(user);
      setIsAuthenticated(true);
      return user;
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred during login');
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/register`, userData);
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred during registration');
      throw error;
    }
  };

  const verifyEmail = async (token) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/verify-email`, { token });
      
      // Si le backend retourne les tokens, connecter automatiquement l'utilisateur
      if (response.data.accessToken && response.data.user) {
        const { accessToken, refreshToken, user } = response.data;
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        
        setCurrentUser(user);
        setIsAuthenticated(true);
      }
      
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'An error occurred during email verification');
      throw error;
    }
  };

  const requestMagicLink = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/magic-link`, { 
        email
      });
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Une erreur est survenue lors de la demande de lien de connexion');
      throw error;
    }
  };

  const verifyMagicLink = async (token) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/verify-magic-link`, { token });
      
      const { accessToken, refreshToken, user } = response.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setCurrentUser(user);
      setIsAuthenticated(true);
      return user;
    } catch (error) {
      setError(error.response?.data?.message || 'Une erreur est survenue lors de la vérification du lien');
      throw error;
    }
  };

  const resendVerificationEmail = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/resend-verification`, { 
        email
      });
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Une erreur est survenue lors du renvoi de l\'email de vérification');
      throw error;
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { 
        email
      });
      return response.data;
    } catch (error) {
      setError(error.response?.data?.message || 'Une erreur est survenue lors de la demande de réinitialisation');
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        // Appeler l'API pour supprimer le refresh token
        await axios.post(`${API_URL}/auth/logout`, { refreshToken });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Continuer même en cas d'erreur
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (userData) => {
    setCurrentUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };

  const refreshQuota = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      await fetchUserData(token);
    }
  };

  const value = {
    currentUser,
    quota,
    isAuthenticated,
    loading,
    error,
    login,
    register,
    verifyEmail,
    requestMagicLink,
    verifyMagicLink,
    resendVerificationEmail,
    forgotPassword,
    logout,
    updateUser,
    refreshQuota
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
